import { addToDictionary, checkText, spellSupported, startSpell, suggestWord } from './client'
import type { Range } from './tokens'

/*
 * Corretor visível num <input>/<textarea>: sublinhado ondulado vermelho nas
 * palavras erradas (uma cópia transparente do texto por cima do campo, que não
 * pega clique) e, no botão direito sobre uma delas, um menu com sugestões,
 * "Adicionar ao dicionário" e "Ignorar". Em palavra certa o botão direito
 * continua sendo o menu normal do navegador.
 * A troca passa por `execCommand('insertText')`: Ctrl+Z desfaz.
 */

type Field = HTMLInputElement | HTMLTextAreaElement

const DEBOUNCE_MS = 250
/** Acima de diálogos e folhas (z-50), abaixo de toasts. */
const Z_OVERLAY = '51'

const COPIED = [
  'fontFamily',
  'fontSize',
  'fontWeight',
  'fontStyle',
  'fontVariantNumeric',
  'letterSpacing',
  'wordSpacing',
  'lineHeight',
  'textTransform',
  'textIndent',
  'textAlign',
  'tabSize',
  'paddingTop',
  'paddingRight',
  'paddingBottom',
  'paddingLeft',
  'borderTopWidth',
  'borderRightWidth',
  'borderBottomWidth',
  'borderLeftWidth',
  'boxSizing',
] as const

/** "Ignorar" vale até fechar a aba. */
const ignored = new Set<string>()
const ignoredKey = (w: string) => w.toLocaleLowerCase('pt-BR')

let openMenu: { close: () => void } | null = null

export function attachSpellcheck(el: Field): () => void {
  if (!spellSupported() || el.readOnly) return () => undefined

  let ranges: Range[] = []
  let checkedValue: string | null = null
  let previousValue = el.value
  let overlay: HTMLDivElement | null = null
  let timer = 0
  let version = 0
  let observer: ResizeObserver | null = null

  const visible = () => ranges.filter((r) => !ignored.has(ignoredKey(r.word)))

  function render() {
    if (document.activeElement !== el || !el.isConnected) return hide()
    const list = visible()
    if (!list.length) return hide()
    if (!overlay) {
      overlay = document.createElement('div')
      overlay.setAttribute('aria-hidden', 'true')
      overlay.dataset.spellOverlay = ''
      document.body.appendChild(overlay)
    }
    const cs = getComputedStyle(el)
    const box = el.getBoundingClientRect()
    const s = overlay.style
    for (const k of COPIED) s[k] = cs[k]
    const isArea = el instanceof HTMLTextAreaElement
    Object.assign(s, {
      position: 'fixed',
      left: `${box.left}px`,
      top: `${box.top}px`,
      width: `${box.width}px`,
      height: `${box.height}px`,
      margin: '0',
      overflow: 'hidden',
      pointerEvents: 'none',
      color: 'transparent',
      background: 'transparent',
      borderStyle: 'solid',
      borderColor: 'transparent',
      zIndex: Z_OVERLAY,
      whiteSpace: isArea ? 'pre-wrap' : 'pre',
      overflowWrap: isArea ? 'break-word' : 'normal',
    })
    const px = (v: string) => parseFloat(v) || 0
    if (isArea) {
      // Barra de rolagem do textarea tira largura do texto.
      const bar = el.offsetWidth - el.clientWidth - px(cs.borderLeftWidth) - px(cs.borderRightWidth)
      s.paddingRight = `${px(cs.paddingRight) + Math.max(0, bar)}px`
    } else {
      // O <input> centraliza a linha na altura do campo.
      s.lineHeight = `${el.clientHeight - px(cs.paddingTop) - px(cs.paddingBottom)}px`
    }

    const text = el.value
    const nodes: Node[] = []
    let at = 0
    for (const r of list) {
      if (r.start < at || text.slice(r.start, r.end) !== r.word) continue
      nodes.push(document.createTextNode(text.slice(at, r.start)))
      const mark = document.createElement('span')
      mark.textContent = r.word
      mark.dataset.start = String(r.start)
      Object.assign(mark.style, {
        textDecorationLine: 'underline',
        textDecorationStyle: 'wavy',
        textDecorationColor: 'hsl(var(--destructive))',
        textDecorationSkipInk: 'none',
        textUnderlineOffset: '3px',
      })
      nodes.push(mark)
      at = r.end
    }
    nodes.push(document.createTextNode(text.slice(at) + (isArea ? '​' : '')))
    overlay.replaceChildren(...nodes)
    overlay.scrollTop = el.scrollTop
    overlay.scrollLeft = el.scrollLeft
  }

  function hide() {
    overlay?.remove()
    overlay = null
  }

  function schedule(delay = DEBOUNCE_MS) {
    window.clearTimeout(timer)
    timer = window.setTimeout(() => void check(), delay)
  }

  async function check() {
    const text = el.value
    const mine = ++version
    try {
      const found = await checkText(text, document.activeElement === el ? (el.selectionEnd ?? -1) : -1)
      if (mine !== version || el.value !== text) return
      ranges = found
      checkedValue = text
      render()
    } catch (err) {
      // Sem dicionário (offline, navegador antigo): fica sem sublinhado.
      if (import.meta.env.DEV) console.warn('[corretor]', err)
    }
  }

  /** Enquanto o worker confere, empurra os sublinhados que estão depois da edição. */
  function shiftRanges() {
    const now = el.value
    const before = previousValue
    previousValue = now
    if (now === before) return
    let from = 0
    while (from < now.length && from < before.length && now[from] === before[from]) from++
    const delta = now.length - before.length
    const editEnd = from + Math.max(0, before.length - now.length)
    ranges = ranges
      .filter((r) => r.end < from || r.start >= editEnd)
      .map((r) => (r.start >= editEnd ? { ...r, start: r.start + delta, end: r.end + delta } : r))
  }

  const onFocus = () => {
    void startSpell().catch(() => undefined)
    previousValue = el.value
    if (checkedValue === el.value) render()
    schedule(0)
    observer = new ResizeObserver(render)
    observer.observe(el)
  }
  const onBlur = () => {
    window.clearTimeout(timer)
    observer?.disconnect()
    observer = null
    hide()
  }
  const onInput = () => {
    shiftRanges()
    render()
    schedule()
  }
  const onScroll = () => render()
  const onWindow = () => {
    if (document.activeElement === el) render()
  }

  function hitAt(x: number, y: number): Range | null {
    if (!overlay) return null
    for (const mark of overlay.querySelectorAll<HTMLElement>('span[data-start]')) {
      for (const rect of mark.getClientRects()) {
        if (x >= rect.left && x <= rect.right && y >= rect.top - 2 && y <= rect.bottom + 4) {
          return visible().find((r) => r.start === Number(mark.dataset.start)) ?? null
        }
      }
    }
    return null
  }

  const onContextMenu = (event: Event) => {
    const e = event as MouseEvent
    const hit = hitAt(e.clientX, e.clientY)
    if (!hit) return
    e.preventDefault()
    showMenu(el, hit, e.clientX, e.clientY, () => {
      ranges = ranges.filter((r) => r !== hit)
      render()
      schedule()
    })
  }

  el.addEventListener('focus', onFocus)
  el.addEventListener('blur', onBlur)
  el.addEventListener('input', onInput)
  el.addEventListener('scroll', onScroll)
  el.addEventListener('contextmenu', onContextMenu)
  window.addEventListener('resize', onWindow)
  window.addEventListener('scroll', onWindow, true)
  if (document.activeElement === el) onFocus()

  return () => {
    el.removeEventListener('focus', onFocus)
    el.removeEventListener('blur', onBlur)
    el.removeEventListener('input', onInput)
    el.removeEventListener('scroll', onScroll)
    el.removeEventListener('contextmenu', onContextMenu)
    window.removeEventListener('resize', onWindow)
    window.removeEventListener('scroll', onWindow, true)
    onBlur()
  }
}

/** Troca a palavra pelo texto escolhido, entrando no desfazer do navegador. */
function replaceWord(el: Field, r: Range, text: string) {
  if (el.value.slice(r.start, r.end) !== r.word) return
  el.focus({ preventScroll: true })
  el.setSelectionRange(r.start, r.end)
  if (!document.execCommand('insertText', false, text)) {
    const setter = Object.getOwnPropertyDescriptor(Object.getPrototypeOf(el) as object, 'value')?.set
    setter?.call(el, el.value.slice(0, r.start) + text + el.value.slice(r.end))
    el.dispatchEvent(new Event('input', { bubbles: true }))
  }
  const caret = r.start + text.length
  el.setSelectionRange(caret, caret)
}

const ITEM =
  'flex w-full cursor-default select-none items-center rounded-sm px-2 py-1.5 text-left text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground disabled:pointer-events-none disabled:opacity-50'

function showMenu(el: Field, r: Range, x: number, y: number, onDone: () => void) {
  openMenu?.close()
  const menu = document.createElement('div')
  menu.setAttribute('role', 'menu')
  menu.setAttribute('aria-label', `Correção de "${r.word}"`)
  menu.className =
    'fixed min-w-[11rem] max-w-[16rem] overflow-hidden rounded-md border border-border bg-popover p-1 text-popover-foreground shadow-md animate-in fade-in-0 zoom-in-95'
  menu.style.zIndex = '60'

  const button = (label: string, onClick: () => void, extra = '') => {
    const b = document.createElement('button')
    b.type = 'button'
    b.setAttribute('role', 'menuitem')
    b.tabIndex = -1
    b.className = `${ITEM} ${extra}`
    b.textContent = label
    b.addEventListener('click', () => {
      close(true)
      onClick()
    })
    return b
  }
  const note = (label: string) => {
    const p = document.createElement('div')
    p.className = 'px-2 py-1.5 text-sm text-muted-foreground'
    p.textContent = label
    return p
  }
  const separator = () => {
    const hr = document.createElement('div')
    hr.setAttribute('role', 'separator')
    hr.className = '-mx-1 my-1 h-px bg-border'
    return hr
  }

  const suggestions = document.createElement('div')
  suggestions.append(note('Procurando sugestões…'))
  menu.append(
    suggestions,
    separator(),
    button('Adicionar ao dicionário', () => {
      void addToDictionary(r.word).finally(onDone)
    }),
    button('Ignorar', () => {
      ignored.add(ignoredKey(r.word))
      onDone()
    }),
  )

  // Dentro de diálogo/folha o menu mora no próprio diálogo: clique nele não fecha o
  // diálogo e o foco não é puxado de volta pela trava de foco.
  const host = el.closest<HTMLElement>('[role="dialog"], [role="alertdialog"]') ?? document.body
  const moved = host !== document.body && getComputedStyle(host).transform !== 'none'
  host.appendChild(menu)
  const place = () => {
    const w = menu.offsetWidth
    const h = menu.offsetHeight
    let left = Math.max(8, Math.min(x, window.innerWidth - w - 8))
    let top = Math.max(8, y + h + 8 > window.innerHeight ? y - h : y)
    if (moved) {
      // Ancestral com transform vira a referência do `fixed`: converte para ele.
      const box = host.getBoundingClientRect()
      menu.style.position = 'absolute'
      left += host.scrollLeft - box.left - host.clientLeft
      top += host.scrollTop - box.top - host.clientTop
    }
    menu.style.left = `${left}px`
    menu.style.top = `${top}px`
  }
  place()

  const items = () => Array.from(menu.querySelectorAll<HTMLButtonElement>('[role="menuitem"]:not(:disabled)'))
  const focusItem = (i: number) => {
    const list = items()
    list[(i + list.length) % list.length]?.focus()
  }

  void suggestWord(r.word)
    .then((list) => {
      if (!menu.isConnected) return
      suggestions.replaceChildren(
        ...(list.length
          ? list.map((sug) => button(sug, () => (replaceWord(el, r, sug), onDone()), 'font-medium'))
          : [note('Sem sugestões')]),
      )
      place()
      focusItem(0)
    })
    .catch(() => menu.isConnected && suggestions.replaceChildren(note('Corretor indisponível')))

  function close(refocus: boolean) {
    // Foca o campo antes de tirar o menu: senão a trava de foco do diálogo leva o foco para outro lugar.
    if (refocus) el.focus({ preventScroll: true })
    menu.remove()
    document.removeEventListener('pointerdown', onOutside, true)
    window.removeEventListener('keydown', onEscape, true)
    window.removeEventListener('blur', onWindowBlur)
    if (openMenu === handle) openMenu = null
  }
  const onOutside = (e: Event) => {
    if (!menu.contains(e.target as Node)) close(false)
  }
  const onWindowBlur = () => close(false)
  // Esc fecha só o menu: escuta na captura da window, antes do diálogo (Radix escuta no document).
  const onEscape = (e: KeyboardEvent) => {
    if (e.key !== 'Escape') return
    e.preventDefault()
    e.stopPropagation()
    close(true)
  }
  menu.addEventListener('keydown', (e) => {
    const i = items().indexOf(document.activeElement as HTMLButtonElement)
    if (e.key === 'ArrowDown') focusItem(i + 1)
    else if (e.key === 'ArrowUp') focusItem(i - 1)
    else if (e.key === 'Home') focusItem(0)
    else if (e.key === 'End') focusItem(-1)
    else if (e.key === 'Tab') close(true)
    else return
    e.preventDefault()
    e.stopPropagation()
  })
  document.addEventListener('pointerdown', onOutside, true)
  window.addEventListener('keydown', onEscape, true)
  window.addEventListener('blur', onWindowBlur)
  const handle = { close: () => close(false) }
  openMenu = handle
  focusItem(0)
}
